import { loadWordList } from "@/lib/tool-kit/vocabulary/anagram";
import { arpabetToIpa, tokenizeText } from "@/lib/tool-kit/vocabulary/ipa";
import { PRONUNCIATIONS_URL } from "@/lib/tool-kit/vocabulary/pronouncing-dictionary";

/**
 * The Spoonerism Generator: swap the opening sounds of two words in a phrase.
 *
 * A spoonerism exchanges the initial sounds of two words — "a lighting of fire" becomes
 * "a fighting of liar" — so everything works on sounds, not spellings. Each word's
 * pronunciation comes from the CMU Pronouncing Dictionary (`public/data/cmu-
 * pronunciations.txt`, ARPAbet). The **onset** is every phoneme before the first vowel
 * (K R in "crushing", N in "knight"); the **rime** is the rest. The swap gives each word
 * the other's onset:
 *
 *   crushing (K R | AH SH IH NG) + blow (B L | OW)  →  B L AH SH IH NG + K R OW
 *
 * Whether a result is a real word is decided on that new sound sequence: it counts if
 * some word in the ENABLE word list is pronounced exactly that way (stress is ignored),
 * so "blushing" and "crow" are found even though their spellings were never swapped
 * letter by letter. When no real word has the sound, a spelling is guessed by swapping
 * the written opening letters, but only for words whose spelling was matched to their
 * onset sounds (knight → kn + ight); otherwise the result is given as a pronunciation.
 * The dictionary records General American English.
 */

export const MAX_WORDS = 12;
export const MAX_GAP = 3;
const RE_VOWEL_PHONE = /[012]$/;
const RE_STRESS = /[012]$/;
const RE_LINE_BREAK = /\r?\n/;
const RE_SPACE = /\s+/;
const RE_UPPER = /^\p{Lu}/u;
const RE_ALL_UPPER = /^\p{Lu}{2,}$/u;

export interface PronSplit {
  /** Phonemes before the first vowel. */
  onset: string[];
  /** The first vowel and everything after it, stress digits kept. */
  rime: string[];
}

/** `"K R AH1 SH IH0 NG"` → onset K R, rime AH1 SH IH0 NG. Null without a vowel. */
export function splitPron(pron: string): PronSplit | null {
  const phones = pron.trim().split(RE_SPACE);
  const vowel = phones.findIndex((phone) => RE_VOWEL_PHONE.test(phone));
  if (vowel === -1) {
    return null;
  }
  return { onset: phones.slice(0, vowel), rime: phones.slice(vowel) };
}

function soundKey(phones: readonly string[]): string {
  return phones.map((phone) => phone.replace(RE_STRESS, "")).join(" ");
}

export interface SpoonerIndex {
  /** ARPAbet of a word as the tokenizer keys it, or null. */
  pron: (word: string) => string | null;
  /** Dictionary words (from the word list) pronounced with these phonemes. */
  sounding: (phones: readonly string[]) => string[];
}

/** The pronunciation file's text and the word list → the lookups the generator needs. */
export function buildSpoonerIndex(
  pronText: string,
  wordList: Iterable<string>
): SpoonerIndex {
  const real = new Set(wordList);
  const prons = new Map<string, string>();
  const bySound = new Map<string, string[]>();
  for (const line of pronText.split(RE_LINE_BREAK)) {
    if (!line || line.startsWith("#")) {
      continue;
    }
    const space = line.indexOf(" ");
    if (space === -1) {
      continue;
    }
    const word = line.slice(0, space);
    const pron = line.slice(space + 1);
    prons.set(word, pron);
    if (real.has(word)) {
      const key = soundKey(pron.split(" "));
      const list = bySound.get(key);
      if (list) {
        list.push(word);
      } else {
        bySound.set(key, [word]);
      }
    }
  }
  return {
    pron: (word) => prons.get(word) ?? null,
    sounding: (phones) => bySound.get(soundKey(phones)) ?? [],
  };
}

/* ------------------------------------------------------------ the spelling */

/**
 * How English spells word-opening consonant sounds. Each written chunk lists the sound
 * sequences it can stand for; a word's opening letters are accepted only if some
 * chunking reproduces its onset exactly.
 */
const GRAPHEMES: Readonly<Record<string, readonly (readonly string[])[]>> = {
  b: [["B"]],
  c: [["K"], ["S"]],
  ch: [["CH"], ["K"], ["SH"]],
  d: [["D"]],
  f: [["F"]],
  g: [["G"], ["JH"]],
  gh: [["G"]],
  gn: [["N"]],
  h: [["HH"]],
  j: [["JH"]],
  k: [["K"]],
  kn: [["N"]],
  l: [["L"]],
  m: [["M"]],
  n: [["N"]],
  p: [["P"]],
  ph: [["F"]],
  pn: [["N"]],
  ps: [["S"]],
  pt: [["T"]],
  q: [["K"]],
  qu: [["K", "W"]],
  r: [["R"]],
  rh: [["R"]],
  s: [["S"]],
  sc: [["S"], ["S", "K"]],
  sch: [["SH"], ["S", "K"]],
  sh: [["SH"]],
  sk: [["S", "K"]],
  t: [["T"]],
  th: [["TH"], ["DH"]],
  v: [["V"]],
  w: [["W"]],
  wh: [["W"], ["HH"]],
  wr: [["R"]],
  x: [["Z"]],
  y: [["Y"]],
  z: [["Z"]],
};
const MAX_CHUNK = 3;

function matchesAt(
  onset: readonly string[],
  at: number,
  sequence: readonly string[]
): boolean {
  return sequence.every((phone, offset) => onset[at + offset] === phone);
}

/** Letters used to spell `onset` at the start of `letters`, or null if they do not. */
function spelledOnsetLength(
  letters: string,
  onset: readonly string[],
  from = 0,
  at = 0
): number | null {
  if (at === onset.length) {
    return from;
  }
  for (let size = MAX_CHUNK; size >= 1; size -= 1) {
    for (const sequence of GRAPHEMES[letters.slice(from, from + size)] ?? []) {
      if (
        sequence.length > onset.length - at ||
        !matchesAt(onset, at, sequence)
      ) {
        continue;
      }
      const end = spelledOnsetLength(
        letters,
        onset,
        from + size,
        at + sequence.length
      );
      if (end !== null) {
        return end;
      }
    }
  }
  return null;
}

export interface Spelling {
  /** The written opening letters: `kn` for knight. */
  head: string;
  /** The rest of the word: `ight`. */
  rest: string;
}

/** A word's spelling split at its onset, or null when the letters do not match the sounds. */
export function splitSpelling(
  word: string,
  onset: readonly string[]
): Spelling | null {
  const length = spelledOnsetLength(word, onset);
  return length === null
    ? null
    : { head: word.slice(0, length), rest: word.slice(length) };
}

/* ------------------------------------------------------------------ results */

export interface NewWord {
  /** The word as it stands in the phrase, in the original's capitalization. */
  text: string;
  /** Broad IPA of the new sound sequence. */
  ipa: string | null;
  /** Some real word has exactly this sound. */
  real: boolean;
  /**
   * `word`: a dictionary word; `guess`: the written opening letters swapped; `sound`: no
   * spelling could be formed, so `text` is the pronunciation between slashes.
   */
  kind: "word" | "guess" | "sound";
  /** Other real words with the same sound. */
  alternates: string[];
}

export interface Spoonerism {
  first: NewWord;
  second: NewWord;
  /** The words as typed, in order. */
  from: [string, string];
  /** The whole phrase with the two words replaced. */
  phrase: string;
  /** The phrase in pieces, so the two new words can be highlighted. */
  parts: { text: string; swapped: boolean }[];
  /** How many of the two new words are real: 0, 1 or 2. */
  realCount: number;
  /** Words between the pair, plus one. */
  distance: number;
}

function matchCase(original: string, word: string): string {
  if (RE_ALL_UPPER.test(original)) {
    return word.toUpperCase();
  }
  return RE_UPPER.test(original)
    ? word.charAt(0).toUpperCase() + word.slice(1)
    : word;
}

function editDistance(a: string, b: string): number {
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i];
    for (let j = 1; j <= b.length; j += 1) {
      row[j] = Math.min(
        previous[j] + 1,
        row[j - 1] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    previous = row;
  }
  return previous[b.length];
}

function pickSpelling(candidates: readonly string[], guess: string | null) {
  return [...candidates].sort(
    (x, y) =>
      (guess ? editDistance(x, guess) - editDistance(y, guess) : 0) ||
      x.length - y.length ||
      x.localeCompare(y)
  );
}

interface Side {
  key: string;
  token: string;
  split: PronSplit;
  spelling: Spelling | null;
}

function sideOf(token: string, key: string, index: SpoonerIndex): Side | null {
  const pron = index.pron(key);
  const split = pron ? splitPron(pron) : null;
  return split
    ? { key, token, split, spelling: splitSpelling(key, split.onset) }
    : null;
}

/** `self` takes `other`'s opening sound. */
function newWord(self: Side, other: Side, index: SpoonerIndex): NewWord {
  const phones = [...other.split.onset, ...self.split.rime];
  const ipa = arpabetToIpa(phones.join(" "));
  const guess =
    self.spelling && other.spelling
      ? other.spelling.head + self.spelling.rest
      : null;
  const found = pickSpelling(
    index.sounding(phones).filter((word) => word !== self.key),
    guess
  );
  if (found.length > 0) {
    return {
      text: matchCase(self.token, found[0]),
      ipa,
      real: true,
      kind: "word",
      alternates: found.slice(1, 4),
    };
  }
  return {
    text: guess ? matchCase(self.token, guess) : `/${ipa ?? phones.join(" ")}/`,
    ipa,
    real: false,
    kind: guess ? "guess" : "sound",
    alternates: [],
  };
}

function usable(a: Side, b: Side): boolean {
  return (
    a.split.onset.length > 0 &&
    b.split.onset.length > 0 &&
    a.split.onset.join(" ") !== b.split.onset.join(" ")
  );
}

export interface SpoonerismSet {
  results: Spoonerism[];
  /** Words in the phrase that the pronunciation dictionary does not have. */
  unknown: string[];
  /** The phrase had more words than the generator reads. */
  truncated: boolean;
}

/**
 * Every spoonerism of one phrase: each pair of words up to `MAX_GAP` positions apart whose
 * opening sounds differ. Best first: both new words real, then the closest pair.
 */
export function spoonerisms(
  phrase: string,
  index: SpoonerIndex
): SpoonerismSet {
  const tokens = tokenizeText(phrase);
  const wordAt: number[] = [];
  tokens.forEach((token, position) => {
    if (token.kind === "word") {
      wordAt.push(position);
    }
  });
  const used = wordAt.slice(0, MAX_WORDS);
  const sides = used.map((position) => {
    const token = tokens[position];
    return token.kind === "word" ? sideOf(token.text, token.key, index) : null;
  });
  const unknown: string[] = [];
  sides.forEach((side, n) => {
    const token = tokens[used[n]];
    if (!side && token.kind === "word" && !unknown.includes(token.text)) {
      unknown.push(token.text);
    }
  });
  const results: Spoonerism[] = [];
  for (let a = 0; a < used.length; a += 1) {
    for (let b = a + 1; b < used.length && b - a <= MAX_GAP; b += 1) {
      const left = sides[a];
      const right = sides[b];
      if (!(left && right && usable(left, right))) {
        continue;
      }
      const first = newWord(left, right, index);
      const second = newWord(right, left, index);
      const parts = tokens.map((token, position) => ({
        text: token.text,
        swapped: position === used[a] || position === used[b],
      }));
      parts[used[a]].text = first.text;
      parts[used[b]].text = second.text;
      results.push({
        first,
        second,
        from: [left.token, right.token],
        phrase: parts.map((part) => part.text).join(""),
        parts,
        realCount: Number(first.real) + Number(second.real),
        distance: b - a,
      });
    }
  }
  results.sort((x, y) => y.realCount - x.realCount || x.distance - y.distance);
  return { results, unknown, truncated: wordAt.length > MAX_WORDS };
}

let loading: Promise<SpoonerIndex> | null = null;

/**
 * The pronunciations and the word list, fetched once per page load. A failed fetch is
 * forgotten, so a retry asks again.
 */
export function loadSpoonerIndex(): Promise<SpoonerIndex> {
  if (!loading) {
    loading = Promise.all([
      fetch(PRONUNCIATIONS_URL).then((response) => {
        if (!response.ok) {
          throw new Error(`Pronunciations returned ${response.status}`);
        }
        return response.text();
      }),
      loadWordList(),
    ])
      .then(([text, words]) => buildSpoonerIndex(text, words))
      .catch((error: unknown) => {
        loading = null;
        throw error;
      });
  }
  return loading;
}
